import classNames from 'classnames';
import React, { useRef, useState, useCallback } from 'react';

export interface CustomTooltipProps {
	children?: React.ReactNode;
	content?: string | JSX.Element[];
	classNameIcon?: string;
	classNameBox?: string;
	toolTipPosition?: ToolTipPosition;
	id?: string;
}

export type ToolTipPosition = 'top-right' | 'top-left' | 'bottom-left' | 'bottom-right';

/**
 * The rectangle the tooltip actually has to fit inside: the viewport, narrowed by any ancestor that
 * clips its overflow.
 *
 * The flip logic used to compare against the viewport alone, which meant a tooltip inside a
 * scrolling panel was never flipped - it was simply cut off at the panel's edge while its
 * viewport-relative rect looked perfectly fine. The booking wizard's filter sidebar
 * (`.filter-sidebar-container`, `overflow: auto` / `overflow-x: hidden`) is where this shows: a
 * passenger-type tooltip opens upwards and is sliced off by the sidebar's top edge.
 *
 * `overflow: visible` ancestors are skipped, so where nothing clips this returns the viewport and
 * the behaviour is exactly what it was.
 */
function getClippingBounds(element: HTMLElement) {
	let top = 0;
	let left = 0;
	let right = window.innerWidth;
	let bottom = window.innerHeight;

	let ancestor = element.parentElement;

	while (ancestor && ancestor !== document.body) {
		const { overflowX, overflowY } = window.getComputedStyle(ancestor);
		const clips = overflowX !== 'visible' || overflowY !== 'visible';

		if (clips) {
			const box = ancestor.getBoundingClientRect();
			top = Math.max(top, box.top);
			left = Math.max(left, box.left);
			right = Math.min(right, box.right);
			bottom = Math.min(bottom, box.bottom);
		}

		ancestor = ancestor.parentElement;
	}

	return { top, left, right, bottom };
}

export function CustomTooltip({
	children,
	classNameIcon = 'icon-information',
	classNameBox,
	toolTipPosition = 'top-right',
	id,
	content,
}: CustomTooltipProps) {
	const tooltipRef = useRef<HTMLSpanElement>(null);
	const [adjustedPosition, setAdjustedPosition] = useState<ToolTipPosition>(toolTipPosition);

	const handlePointerDown = (e: React.PointerEvent | React.TouchEvent | React.KeyboardEvent) => {
		e.stopPropagation();
	};

	const handleMouseEnter = useCallback(() => {
		if (!tooltipRef.current) return;

		const tooltip = tooltipRef.current;
		const rect = tooltip.getBoundingClientRect();
		const bounds = getClippingBounds(tooltip);

		let newPosition = toolTipPosition;

		const offTop = rect.top < bounds.top;
		const offBottom = rect.bottom > bounds.bottom;
		const offLeft = rect.left < bounds.left;
		const offRight = rect.right > bounds.right;

		if (offTop && newPosition.startsWith('top')) {
			newPosition = newPosition.replace('top', 'bottom') as ToolTipPosition;
		} else if (offBottom && newPosition.startsWith('bottom')) {
			newPosition = newPosition.replace('bottom', 'top') as ToolTipPosition;
		}

		if (offLeft && newPosition.endsWith('left')) {
			newPosition = newPosition.replace('left', 'right') as ToolTipPosition;
		} else if (offRight && newPosition.endsWith('right')) {
			newPosition = newPosition.replace('right', 'left') as ToolTipPosition;
		}

		if (newPosition !== adjustedPosition) {
			setAdjustedPosition(newPosition);
		}
	}, [toolTipPosition, adjustedPosition]);

	const handleMouseLeave = useCallback(() => {
		setAdjustedPosition(toolTipPosition);
	}, [toolTipPosition]);

	return (
		<span
			id={id}
			className={classNames('custom-tooltip icon-right', classNameIcon)}
			onPointerDown={handlePointerDown}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<span ref={tooltipRef} className={classNames('tooltiptext', classNameBox, adjustedPosition)}>
				{children ?? content}
			</span>
		</span>
	);
}
